import {Plural, Trans} from '@lingui/macro';
import _, {noop, size} from 'lodash';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {ColumnWidthsEnum} from '../../../libs/contacts-manager/src/lib/ui/web/tables/common/sharedTableConfig';
import {AsyncHandlerOf, FrontClickEvent} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {arrayToggle} from '../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {SimpleDropdown} from '../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {SimpleDropdownItemWithColor} from '../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItemWithColor';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {buildIndexMap} from '../../../srcCommon/helpers/itemIndexHelpers';
import {ScrollDirectionsEnum} from '../../../srcCommon/scroll/scrollerTypes';
import {OkCancelDialog} from '../../components/common/dialogs/okCancelDialog';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {SnoozeCell} from '../../components/common/table/cells/snoozeCell';
import {
  VirtualTableColumnCellsEnum,
  VirtualTableMultiSelectStatesEnum,
} from '../../components/common/table/helpers/tableHelpers';
import {StaticVirtualTable} from '../../components/common/table/staticVirtualTable';
import {TableColumn} from '../../components/common/table/tableColumn';
import {TableMultiSelectHeaderRow} from '../../components/common/table/tableMultiSelectHeaderRow';
import {TableRow} from '../../components/common/table/tableRow';
import {VirtualTableRowRenderer} from '../../components/common/table/virtualTableBase';
import {getSelectedIdsOnClick} from '../../helpers/selectionHelpers';
import {SettingsCustomSnoozeModel} from '../../models/api/customSnoozeModel';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {SettingsResourcePagesEnum, SettingsSnoozesUrl} from '../../models/url/settingsUrlModel';
import {AsyncMoreStatusesEnum, ResourceMap} from '../../store/appTypes';

/*
 * Props.
 */

interface SettingsSnoozesTableProps {
  snoozes: ResourceMap<SettingsCustomSnoozeModel>;
  snoozesUrl: SettingsSnoozesUrl;
  snoozesIds: ReadonlyArray<number>;
  onSingleDeleteClick: AsyncHandlerOf<SettingsCustomSnoozeModel>;
  onBulkDeleteClick: AsyncHandlerOf<ReadonlyArray<number>>;
}

/*
 * Types.
 */

enum SnoozesTableColumnsEnum {
  NAME = 'name',
  OFFSET = 'offset',
}

const snoozesTableNameColumnId = 'signatures-table-name';

const snoozesTableColumns: ReadonlyArray<TableColumn<SnoozesTableColumnsEnum>> = [
  {
    id: snoozesTableNameColumnId,
    title: <Trans id="snoozes-table-column-name">Name</Trans>,
    type: SnoozesTableColumnsEnum.NAME,
    cellType: VirtualTableColumnCellsEnum.TEXT,
    settings: {
      cellWidth: {
        shouldFillRemainingWidth: true,
        minWidth: ColumnWidthsEnum.LARGE,
      },
    },
  },
];

/*
 * Style.
 */

const StyledTableContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
`;

const StyledEmptyStateContainerDiv = styled.div`
  position: absolute;
  top: 80px;
  left: 0;
  right: 0;
  color: ${(p) => p.theme.greys.shade70};
  text-align: center;
`;

const StyledTableRow = styled(TableRow)`
  min-height: 56px;
  background: ${(p) => p.theme.greys.white};
`;

const StyledButtonsWrapper = styled.div`
  display: flex;
  flex-flow: row;
  gap: 8px;
`;

/*
 * Component.
 */

export const SettingsSnoozesTable: FC<SettingsSnoozesTableProps> = (props) => {
  const {snoozes, snoozesIds, snoozesUrl} = props;
  const {navigateToLink} = useLinkNavigation();
  const [snoozeIdWithDropdownOpened, setSnoozeIdWithDropdownOpened] = useState(-1);
  const [snoozeIdToDelete, setSnoozeIdToDelete] = useState(-1);
  const [selectedIds, setSelectedIds] = useState<ReadonlyArray<number>>([]);
  const [isMassDeleteDialogOpen, setIsMassDeleteDialogOpen] = useState(false);

  const toggleSelectedSnooze = (snooze: SettingsCustomSnoozeModel, e: FrontClickEvent<HTMLElement>) => {
    e.preventDefault();
    const snoozeIds = _(snoozes)
      .values()
      .compact()
      .map((s) => s.id)
      .value();
    setSelectedIds((prevSelected) => {
      if (!e || (!e?.shiftKey && !e?.metaKey)) {
        return arrayToggle(prevSelected, snooze.id);
      }

      return getSelectedIdsOnClick(prevSelected, snoozeIds, buildIndexMap(snoozeIds), snooze.id, e);
    });
  };

  const renderRow: VirtualTableRowRenderer<number> = (snoozeId: number, options, siblingData) => {
    const snooze = snoozes[snoozeId];
    if (!snooze) {
      return null;
    }

    return (
      <StyledTableRow
        onCheckBoxClick={(e) => toggleSelectedSnooze(snooze, e)}
        onSelect={(e) => {
          if (e.defaultPrevented) {
            return;
          }

          if (selectedIds.length > 0 || e.shiftKey || e.metaKey) {
            toggleSelectedSnooze(snooze, e);
            return;
          }

          navigateToLink(
            buildSettingsPath({
              ...snoozesUrl,
              resourcePage: SettingsResourcePagesEnum.EDIT,
              snoozeId: snooze.id,
            }),
          );
        }}
        hasHover
        isSelected={selectedIds.includes(snoozeId)}
        shouldAllowMultiSelect={options.shouldAllowMultiSelect}
        isCheckboxesVisible={selectedIds.length > 0}
        renderColumn={(isHovering) => (
          <SnoozeCell
            shouldRemoveTopBorderRadius={selectedIds.includes(siblingData?.previous ?? -1)}
            shouldRemoveBottomBorderRadius={selectedIds.includes(siblingData?.next ?? -1)}
            isSelected={selectedIds.includes(snoozeId)}
            size={options.size}
            key={snoozesTableColumns[0].type}
            value={snooze}
            settings={snoozesTableColumns[0].settings}
            isHovering={isHovering}
            index={0}
            avatarSize={VisualSizesEnum.LARGE}
            hasHover={snoozeId === snoozeIdWithDropdownOpened}
            onActionsDropdownOpened={(isOpen) => setSnoozeIdWithDropdownOpened(isOpen ? snoozeId : -1)}
            renderActionsDropdown={({modalProps}) => (
              <SimpleDropdown {...modalProps} placement="bottom-end">
                <SimpleDropdownItem
                  onSelect={() => {
                    navigateToLink(
                      buildSettingsPath({
                        ...snoozesUrl,
                        resourcePage: SettingsResourcePagesEnum.EDIT,
                        snoozeId: snooze.id,
                      }),
                    );
                  }}
                >
                  <Trans id="snoozestable-edit">Edit snooze</Trans>
                </SimpleDropdownItem>
                <SimpleDropdownItemWithColor
                  color={PaletteColorsEnum.RED}
                  onSelect={() => {
                    setSnoozeIdToDelete(snoozeId);
                  }}
                >
                  <Trans id="snoozestable-delete">Delete</Trans>
                </SimpleDropdownItemWithColor>
              </SimpleDropdown>
            )}
          />
        )}
      />
    );
  };

  const renderEmptyState = () => (
    <StyledEmptyStateContainerDiv>
      <Trans id="snoozes-table-empty-state">No snoozes</Trans>
    </StyledEmptyStateContainerDiv>
  );

  const maybeRenderDeleteDialog = () => {
    const snoozeToDelete = snoozes[snoozeIdToDelete];
    if (!snoozeToDelete) {
      return null;
    }

    const onDeleteSnooze = async () => {
      setSnoozeIdToDelete(-1);
      await props.onSingleDeleteClick(snoozeToDelete);
    };

    return (
      <OkCancelDialog
        title={<Trans id="snoozestable-deletethissnooze">Delete this snooze</Trans>}
        okButtonLabel={
          <Trans id="delete-snoozes-confirmation-dialog-delete-permanently">Delete permanently</Trans>
        }
        onRequestClose={() => setSnoozeIdToDelete(-1)}
        onOkClick={onDeleteSnooze}
      >
        <Trans id="snoozestable-delete-copy">
          This snooze will be permanently deleted. This action cannot be undone.
        </Trans>
      </OkCancelDialog>
    );
  };

  const maybeRenderMassDeleteDialog = () => {
    if (!isMassDeleteDialogOpen) {
      return null;
    }

    const onDeleteSnooze = async () => {
      setSnoozeIdToDelete(-1);
      setIsMassDeleteDialogOpen(false);
      setSelectedIds([]);
      await props.onBulkDeleteClick(selectedIds);
    };

    return (
      <OkCancelDialog
        title={<Trans id="snoozestable-deletemany">Delete {selectedIds.length} snoozes</Trans>}
        okButtonLabel={
          <Trans id="delete-snoozes-confirmation-dialog-delete-permanently">Delete permanently</Trans>
        }
        onRequestClose={() => {
          setSnoozeIdToDelete(-1);
          setIsMassDeleteDialogOpen(false);
        }}
        onOkClick={onDeleteSnooze}
      >
        <Trans id="snoozestable-deletemany-copy">
          These snoozes will be permanently deleted. This action cannot be undone.
        </Trans>
      </OkCancelDialog>
    );
  };

  const numberOfSnoozesInList = useMemo(() => size(snoozes), [snoozes]);
  const hasAllSelected = useMemo(
    () => selectedIds.length === numberOfSnoozesInList,
    [selectedIds.length, numberOfSnoozesInList],
  );

  const hasSomeSelected = useMemo(() => {
    if (!selectedIds.length) {
      return false;
    }

    return numberOfSnoozesInList > selectedIds.length;
  }, [selectedIds.length, numberOfSnoozesInList]);

  const multiSelectCheckboxState: VirtualTableMultiSelectStatesEnum = useMemo(() => {
    if (hasSomeSelected) {
      return VirtualTableMultiSelectStatesEnum.INDETERMINATE;
    }

    return hasAllSelected
      ? VirtualTableMultiSelectStatesEnum.CHECKED
      : VirtualTableMultiSelectStatesEnum.UNCHECKED;
  }, [hasAllSelected, hasSomeSelected]);

  return (
    <>
      <StyledTableContainerDiv>
        <StaticVirtualTable<number>
          columns={snoozesTableColumns}
          items={snoozesIds}
          hasData={false}
          shouldRenderEmptyState={snoozesIds.length === 0}
          onLoadMore={noop}
          renderRow={renderRow}
          renderEmpty={renderEmptyState}
          shouldAllowMultiSelect
          onMultiSelectHeaderChange={(check) => {
            if (check) {
              setSelectedIds(snoozesIds);
            } else {
              setSelectedIds([]);
            }
          }}
          isInMultiSelectMode={selectedIds.length > 0}
          moreStatuses={{
            [ScrollDirectionsEnum.TOP]: AsyncMoreStatusesEnum.ALL_LOADED,
            [ScrollDirectionsEnum.BOTTOM]: AsyncMoreStatusesEnum.ALL_LOADED,
          }}
          renderMultiSelectHeader={(onMultiSelectChange) => (
            <TableMultiSelectHeaderRow
              multiSelectStatus={multiSelectCheckboxState}
              onMultiSelectChange={onMultiSelectChange}
              renderTotalItemsLabel={() => (
                <Plural
                  id="settings-snoozes-selected"
                  value={selectedIds.length}
                  zero="No snoozes selected"
                  one="1 snooze selected"
                  other="# snoozes selected"
                />
              )}
            >
              <StyledButtonsWrapper>
                <Button
                  variant={ButtonVariantsEnum.SECONDARY_DANGER}
                  onClick={() => setIsMassDeleteDialogOpen(true)}
                >
                  <Trans id="settingstags-bulk-delete">Delete</Trans>
                </Button>
              </StyledButtonsWrapper>
            </TableMultiSelectHeaderRow>
          )}
        />
      </StyledTableContainerDiv>
      {maybeRenderDeleteDialog()}
      {maybeRenderMassDeleteDialog()}
    </>
  );
};
